import React, { useEffect, useMemo, useState } from 'react';
import { Check, Mail, PhoneCall, Copy, Sparkles } from 'lucide-react';

interface ConfettiPiece {
  id: number;
  left: number;
  duration: number;
  size: number;
  delay: number;
  color: string;
  shape: 'square' | 'circle' | 'strip';
  rotate: number;
}

const CONFETTI_COLORS = [
  'bg-primary',
  'bg-secondary',
  'bg-emerald-400',
  'bg-amber-400',
  'bg-pink-400',
  'bg-sky-400'
];

const CONFETTI_SHAPES: ConfettiPiece['shape'][] = ['square', 'circle', 'strip'];

const MncapMedicalSuccess: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const referenceNumber = useMemo(
    () =>
      `MED-${new Date().getFullYear()}-${Math.random()
        .toString(36)
        .slice(2, 11)
        .toUpperCase()}`,
    []
  );

  const confetti = useMemo<ConfettiPiece[]>(
    () =>
      Array.from({ length: 70 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        duration: 3 + Math.random() * 4,
        size: 6 + Math.random() * 10,
        delay: Math.random() * 1.5,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        shape: CONFETTI_SHAPES[Math.floor(Math.random() * CONFETTI_SHAPES.length)],
        rotate: Math.random() * 360
      })),
    []
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referenceNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  const steps = [
    {
      icon: Mail,
      title: 'Confirmation par email',
      description: 'Dans les prochaines minutes'
    },
    {
      icon: PhoneCall,
      title: 'Prise de contact',
      description: 'Si besoin d\'informations complémentaires'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary/20 blur-3xl animate-blob" />
      <div
        className="pointer-events-none absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-secondary/20 blur-3xl animate-blob"
        style={{ animationDelay: '3s' }}
      />
      <div
        className="pointer-events-none absolute top-1/4 right-1/4 w-72 h-72 rounded-full bg-emerald-300/20 blur-3xl animate-blob"
        style={{ animationDelay: '6s' }}
      />

      {confetti.map(piece => {
        const shapeClasses =
          piece.shape === 'circle'
            ? 'rounded-full'
            : piece.shape === 'strip'
              ? 'rounded-sm'
              : 'rounded-[2px]';
        const height =
          piece.shape === 'strip' ? piece.size * 2.5 : piece.size;
        return (
          <div
            key={piece.id}
            className={`absolute ${piece.color} ${shapeClasses} opacity-80 animate-confetti-fall shadow-sm`}
            style={{
              left: `${piece.left}%`,
              width: `${piece.size}px`,
              height: `${height}px`,
              animationDuration: `${piece.duration}s`,
              animationDelay: `${piece.delay}s`,
              top: '-30px',
              ['--rotate-start' as any]: `${piece.rotate}deg`
            }}
          />
        );
      })}

      <div className="relative max-w-lg w-full z-10">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl ring-1 ring-black/5 p-8 sm:p-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/10 rounded-full -mr-20 -mt-20" />
          <div className="absolute bottom-0 left-0 w-28 h-28 bg-secondary/10 rounded-full -ml-14 -mb-14" />

          <div className="relative z-10">
            <div className="flex justify-center mb-8">
              <div className="relative">
                <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping-slow" />
                <span
                  className="absolute inset-0 rounded-full bg-primary/10 animate-ping-slow"
                  style={{ animationDelay: '0.6s' }}
                />
                <div className="relative bg-primary rounded-full p-5 shadow-lg animate-bounce-slow">
                  <Check className="w-14 h-14 text-white" strokeWidth={3} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-primary animate-pulse" />
              <span className="uppercase tracking-widest text-xs font-semibold text-primary">
                Succès
              </span>
              <Sparkles className="w-5 h-5 text-secondary animate-pulse" />
            </div>

            <h1 className="text-3xl sm:text-4xl font-extrabold text-center mb-4 text-gray-900">
              Formulaire envoyé !
            </h1>

            <div className="space-y-3 mb-8">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 p-3 rounded-xl bg-gray-50 ring-1 ring-gray-100 hover:ring-primary/30 transition"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <step.icon className="w-5 h-5 text-primary" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {step.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                    {i + 1}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-primary/5 rounded-2xl p-4 mb-6 ring-1 ring-primary/15">
              <p className="text-xs uppercase tracking-wider text-gray-500 text-center mb-2">
                Numéro de référence
              </p>
              <div className="flex items-center justify-center gap-3">
                <p className="text-lg sm:text-xl font-mono font-bold text-gray-900 tracking-wider">
                  {referenceNumber}
                </p>
                <button
                  onClick={handleCopy}
                  type="button"
                  className="p-2 rounded-lg hover:bg-primary/10 transition text-gray-500 hover:text-primary"
                  aria-label="Copier le numéro de référence"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p
                className={`text-xs text-center mt-2 transition-opacity ${
                  copied ? 'opacity-100 text-emerald-600' : 'opacity-0'
                }`}
              >
                Copié dans le presse-papier
              </p>
            </div>

          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          Une question ? Contactez votre conseiller.
        </p>
      </div>

      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(-20px) rotate(var(--rotate-start, 0deg));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: translateY(110vh) rotate(calc(var(--rotate-start, 0deg) + 720deg));
            opacity: 0;
          }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.6; }
          80%, 100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.1); }
          66% { transform: translate(-20px, 30px) scale(0.95); }
        }
        .animate-confetti-fall {
          animation: confetti-fall linear infinite;
        }
        .animate-bounce-slow {
          animation: bounce-slow 2.5s ease-in-out infinite;
        }
        .animate-ping-slow {
          animation: ping-slow 2.2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .animate-blob {
          animation: blob 9s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default MncapMedicalSuccess;
